import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import type { ValidationCheckResult, ValidationIssue } from '../types';
import {
  buildValidationIssueFamilies,
  type ValidationIssueFamily,
  type ValidationIssueFamilyCategory,
  type ValidationIssueFamilySeverity,
} from '../lib/validationIssueFamilies';
import { buildEffectiveValidationCheckResults } from '../lib/validationConditionStudioModel';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { webLog } from '../../../shared/api/grpc/webLogger';

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function formatMs(ms: number): string {
  return ms.toFixed(1);
}

export type ValidationSeverityFilter = 'all' | ValidationIssueFamilySeverity;

export type ValidationBlockerFilter = 'all' | 'blocking';

const ISSUE_SYSTEM_NAME_KEYS = [
  'systemName',
  'sourceSystemName',
  'systemTypeName',
  'system',
  'sourceSystemNames',
] as const;

function normalizeSystemScopeToken(value: string | undefined): string {
  return String(value || '').trim().toLowerCase();
}

function isGlobalSystemScopeName(value: string | undefined): boolean {
  const normalized = normalizeSystemScopeToken(value);
  return normalized === 'all systems' || normalized === '(unassigned system)';
}

function parseSystemScopeTokens(raw: string | undefined): string[] {
  const value = String(raw || '').trim();
  if (!value) {
    return [];
  }

  const multipleSystemsPrefix = 'Multiple systems (';
  const normalized =
    value.startsWith(multipleSystemsPrefix) && value.endsWith(')')
      ? value.slice(multipleSystemsPrefix.length, -1)
      : value;

  return normalized
    .split(/[;,|]+/)
    .map((entry) => entry.replace(/(?:\.\.\.|…)/g, '').trim())
    .filter((entry) => entry.length > 0);
}

function readIssueAdditionalDataValue(issue: ValidationIssue, key: string): string {
  const data = issue.AdditionalData;
  if (!data) {
    return '';
  }

  const direct = data[key];
  if (typeof direct === 'string' && direct.trim()) {
    return direct;
  }

  const targetKey = key.toLowerCase();
  const matchedKey = Object.keys(data).find((candidate) => candidate.toLowerCase() === targetKey);
  const matchedValue = matchedKey ? data[matchedKey] : undefined;
  return typeof matchedValue === 'string' ? matchedValue : '';
}

function collectIssueSystemNames(issue: ValidationIssue): Set<string> {
  const tokens = new Set<string>();

  for (const key of ISSUE_SYSTEM_NAME_KEYS) {
    parseSystemScopeTokens(readIssueAdditionalDataValue(issue, key)).forEach((name) => {
      const normalized = normalizeSystemScopeToken(name);
      if (normalized && !isGlobalSystemScopeName(normalized)) {
        tokens.add(normalized);
      }
    });
  }

  return tokens;
}

function issueMatchesFocusScope(
  issue: ValidationIssue,
  focusedElementIds: Set<string>,
  focusedSystemNames: Set<string>
): boolean {
  if (
    focusedElementIds.size > 0 &&
    Array.isArray(issue.ElementIds) &&
    issue.ElementIds.some((id) => focusedElementIds.has(String(id)))
  ) {
    return true;
  }

  if (focusedSystemNames.size === 0) {
    return false;
  }

  return Array.from(collectIssueSystemNames(issue)).some((name) => focusedSystemNames.has(name));
}

function filterChecksByFocusScope(
  checkResults: ValidationCheckResult[] | undefined,
  focusedElementIds?: string[],
  focusedSystemNames?: string[]
): ValidationCheckResult[] {
  if (!checkResults || checkResults.length === 0) {
    return [];
  }

  const focusedElementIdSet = new Set(
    (focusedElementIds ?? [])
      .map((id) => String(id).trim())
      .filter((id) => id.length > 0)
  );
  const focusedSystemNameSet = new Set(
    (focusedSystemNames ?? [])
      .map(normalizeSystemScopeToken)
      .filter((name) => name.length > 0 && !isGlobalSystemScopeName(name))
  );

  if (focusedElementIdSet.size === 0 && focusedSystemNameSet.size === 0) {
    return checkResults;
  }

  return checkResults.map((check) => {
    const filteredIssues = check.Issues.filter((issue) =>
      issueMatchesFocusScope(issue, focusedElementIdSet, focusedSystemNameSet)
    );
    const hasIssues = filteredIssues.length > 0;

    return {
      ...check,
      Issues: filteredIssues,
      IssueCount: filteredIssues.length,
      HasIssues: hasIssues,
      Status: hasIssues ? check.Status : 'Passed',
    } satisfies ValidationCheckResult;
  });
}

function matchesSearch(family: ValidationIssueFamily, query: string): boolean {
  if (!query) {
    return true;
  }

  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return [
    family.title,
    family.summary,
    family.targetLabel,
    family.affectedItems.join(' '),
    family.failingTypes.map((item) => item.componentType).join(' '),
    family.failingInstances.map((item) => `${item.label} ${item.reason}`).join(' '),
    family.examples.map((example) => `${example.label} ${example.detail}`).join(' '),
  ].some((value) => value.toLowerCase().includes(normalizedQuery));
}

export interface ValidationWorkspaceState {
  allFamilies: ValidationIssueFamily[];
  filteredFamilies: ValidationIssueFamily[];
  passedChecks: ValidationCheckResult[];
  visiblePassedChecks: ValidationCheckResult[];
  selectedFamilyId: string | null;
  selectedFamily: ValidationIssueFamily | null;
  setSelectedFamilyId: (id: string | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  severityFilter: ValidationSeverityFilter;
  setSeverityFilter: (filter: ValidationSeverityFilter) => void;
  blockerFilter: ValidationBlockerFilter;
  setBlockerFilter: (filter: ValidationBlockerFilter) => void;
  requiredOnly: boolean;
  setRequiredOnly: (value: boolean) => void;
  categoryFilter: ValidationIssueFamilyCategory | 'all';
  setCategoryFilter: (filter: ValidationIssueFamilyCategory | 'all') => void;
  showPassedOnly: boolean;
  setShowPassedOnly: (value: boolean) => void;
  passedCollapsed: boolean;
  setPassedCollapsed: (value: boolean) => void;
  unresolvedFamilyCount: number;
  totalIssueCount: number;
  severityCounts: Record<ValidationSeverityFilter, number>;
  categoryCounts: Record<ValidationIssueFamilyCategory, number>;
  blockerCount: number;
  requiredOnlyCount: number;
}

export interface ValidationWorkspaceInitialState {
  selectedFamilyId?: string | null;
  searchQuery?: string;
  severityFilter?: ValidationSeverityFilter;
  blockerFilter?: ValidationBlockerFilter;
  requiredOnly?: boolean;
  categoryFilter?: ValidationIssueFamilyCategory | 'all';
  showPassedOnly?: boolean;
  passedCollapsed?: boolean;
}

function resolveFamilyDisplaySeverity(family: ValidationIssueFamily): ValidationIssueFamilySeverity {
  return family.severity;
}

function sumFamilyIssues(families: ValidationIssueFamily[]): number {
  return families.reduce((sum, family) => sum + family.issueCount, 0);
}

export function useValidationWorkspace(
  checkResults: ValidationCheckResult[] | undefined,
  focusedElementIds?: string[],
  documentNameOrInitialState?: string | ValidationWorkspaceInitialState,
  initialStateArg?: ValidationWorkspaceInitialState,
  focusedSystemNames?: string[]
): ValidationWorkspaceState {
  const conditionSets = usePcfValidationStore((store) => store.conditionSets);
  const customValidationPoints = usePcfValidationStore((store) => store.customValidationPoints);
  const documentName =
    typeof documentNameOrInitialState === 'string' ? documentNameOrInitialState : undefined;
  const initialState =
    typeof documentNameOrInitialState === 'string' ? initialStateArg : documentNameOrInitialState;
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(
    initialState?.selectedFamilyId ?? null
  );
  const [searchQuery, setSearchQuery] = useState(initialState?.searchQuery ?? '');
  const [severityFilter, setSeverityFilter] = useState<ValidationSeverityFilter>(
    initialState?.severityFilter ?? 'all'
  );
  const [blockerFilter, setBlockerFilter] = useState<ValidationBlockerFilter>(
    initialState?.blockerFilter ?? 'all'
  );
  const [requiredOnly, setRequiredOnly] = useState(initialState?.requiredOnly ?? false);
  const [categoryFilter, setCategoryFilter] = useState<ValidationIssueFamilyCategory | 'all'>(
    initialState?.categoryFilter ?? 'all'
  );
  const [showPassedOnly, setShowPassedOnly] = useState(initialState?.showPassedOnly ?? false);
  const [passedCollapsed, setPassedCollapsed] = useState(
    initialState?.passedCollapsed ?? true
  );
  const deferredCheckResults = useDeferredValue(checkResults);
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const scopedChecks = useMemo(
    () => filterChecksByFocusScope(deferredCheckResults, focusedElementIds, focusedSystemNames),
    [deferredCheckResults, focusedElementIds, focusedSystemNames]
  );

  const effectiveChecks = useMemo(
    () =>
      buildEffectiveValidationCheckResults({
        checkResults: scopedChecks,
        conditionSets,
        customValidationPoints,
        documentName,
      }),
    [scopedChecks, conditionSets, customValidationPoints, documentName]
  );

  const { families, passedChecks } = useMemo(() => {
    const startedAt = nowMs();
    const result = buildValidationIssueFamilies(effectiveChecks);
    const elapsedMs = nowMs() - startedAt;
    const issueCount = effectiveChecks.reduce(
      (total, check) => total + (Array.isArray(check.Issues) ? check.Issues.length : 0),
      0
    );

    if (elapsedMs > 100) {
      webLog.warn(
        'validation-performance',
        `issue-family-build: elapsedMs=${formatMs(elapsedMs)} checks=${effectiveChecks.length} issues=${issueCount} families=${result.families.length}`
      );
    } else if (elapsedMs > 25 || issueCount > 100) {
      webLog.info(
        'validation-performance',
        `issue-family-build: elapsedMs=${formatMs(elapsedMs)} checks=${effectiveChecks.length} issues=${issueCount} families=${result.families.length}`
      );
    }

    return result;
  }, [effectiveChecks]);

  const filteredFamilies = useMemo(
    () =>
      families.filter((family) => {
        if (severityFilter !== 'all' && resolveFamilyDisplaySeverity(family) !== severityFilter) {
          return false;
        }

        if (blockerFilter === 'blocking' && !family.isBlocking) {
          return false;
        }

        if (requiredOnly && !family.isRequiredOnly) {
          return false;
        }

        if (categoryFilter !== 'all' && family.category !== categoryFilter) {
          return false;
        }

        return matchesSearch(family, deferredSearchQuery);
      }),
    [families, severityFilter, blockerFilter, requiredOnly, categoryFilter, deferredSearchQuery]
  );

  const visiblePassedChecks = useMemo(() => {
    const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return passedChecks;
    }

    return passedChecks.filter((check) =>
      [check.Title, check.Description]
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [passedChecks, deferredSearchQuery]);

  useEffect(() => {
    if (showPassedOnly) {
      return;
    }

    if (filteredFamilies.length === 0) {
      setSelectedFamilyId(null);
      return;
    }

    if (!selectedFamilyId || !filteredFamilies.some((family) => family.id === selectedFamilyId)) {
      setSelectedFamilyId(filteredFamilies[0].id);
    }
  }, [filteredFamilies, selectedFamilyId, showPassedOnly]);

  const selectedFamily = useMemo(
    () => {
      if (showPassedOnly) {
        return null;
      }

      return filteredFamilies.find((family) => family.id === selectedFamilyId) ?? null;
    },
    [filteredFamilies, selectedFamilyId, showPassedOnly]
  );

  const totalIssueCount = useMemo(
    () => families.reduce((sum, family) => sum + family.issueCount, 0),
    [families]
  );

  const severityCounts = useMemo(
    () => ({
      all: totalIssueCount,
      critical: sumFamilyIssues(
        families.filter((family) => resolveFamilyDisplaySeverity(family) === 'critical')
      ),
      error: sumFamilyIssues(
        families.filter((family) => resolveFamilyDisplaySeverity(family) === 'error')
      ),
      warning: sumFamilyIssues(
        families.filter((family) => resolveFamilyDisplaySeverity(family) === 'warning')
      ),
      info: sumFamilyIssues(
        families.filter((family) => resolveFamilyDisplaySeverity(family) === 'info')
      ),
    }),
    [families, totalIssueCount]
  );

  const categoryCounts = useMemo(
    () => ({
      mapping: sumFamilyIssues(families.filter((family) => family.category === 'mapping')),
      'required-export': sumFamilyIssues(families.filter((family) => family.category === 'required-export')),
      connectors: sumFamilyIssues(families.filter((family) => family.category === 'connectors')),
      properties: sumFamilyIssues(families.filter((family) => family.category === 'properties')),
      components: sumFamilyIssues(families.filter((family) => family.category === 'components')),
    }),
    [families]
  );

  return {
    allFamilies: families,
    filteredFamilies,
    passedChecks,
    visiblePassedChecks,
    selectedFamilyId,
    selectedFamily,
    setSelectedFamilyId,
    searchQuery,
    setSearchQuery,
    severityFilter,
    setSeverityFilter,
    blockerFilter,
    setBlockerFilter,
    requiredOnly,
    setRequiredOnly,
    categoryFilter,
    setCategoryFilter,
    showPassedOnly,
    setShowPassedOnly,
    passedCollapsed,
    setPassedCollapsed,
    unresolvedFamilyCount: families.length,
    totalIssueCount,
    severityCounts,
    categoryCounts,
    blockerCount: sumFamilyIssues(families.filter((family) => family.isBlocking)),
    requiredOnlyCount: sumFamilyIssues(families.filter((family) => family.isRequiredOnly)),
  };
}
